// Generic Thrift compact-protocol codec for Parquet footers. It keeps every field id, wire type and list
// element type of the source, so re-encoding an unchanged footer reproduces it and a patched footer
// differs only where it was patched.

const COMPACT_BOOL_TRUE = 1;
const COMPACT_BOOL_FALSE = 2;
const COMPACT_BYTE = 3;
const COMPACT_I16 = 4;
const COMPACT_I32 = 5;
const COMPACT_I64 = 6;
const COMPACT_DOUBLE = 7;
const COMPACT_BINARY = 8;
const COMPACT_LIST = 9;
const COMPACT_SET = 10;
const COMPACT_MAP = 11;
const COMPACT_STRUCT = 12;

export type ThriftNode =
  | { type: 'bool'; value: boolean; listByte?: number }
  | { type: 'byte'; value: number }
  | { type: 'i16' | 'i32'; value: number }
  | { type: 'i64'; varint: Uint8Array }
  | { type: 'double'; bytes: Uint8Array }
  | { type: 'binary'; bytes: Uint8Array }
  | { type: 'list' | 'set'; elementType: number; items: ThriftNode[] }
  | { type: 'map'; keyType: number; valueType: number; entries: Array<[ThriftNode, ThriftNode]> }
  | ThriftStruct;

export interface ThriftField {
  id: number;
  node: ThriftNode;
}

export interface ThriftStruct {
  type: 'struct';
  fields: ThriftField[];
}

const MAX_DEPTH = 64;

class Reader {
  private position = 0;

  constructor(private readonly bytes: Uint8Array) {}

  get offset() {
    return this.position;
  }

  private readByte(): number {
    if (this.position >= this.bytes.length) {
      throw new Error('Unexpected end of Thrift data');
    }
    return this.bytes[this.position++];
  }

  private readVarintNumber(): number {
    let result = 0;
    let multiplier = 1;
    for (let index = 0; index < 5; index += 1) {
      const byte = this.readByte();
      result += (byte & 0x7f) * multiplier;
      if ((byte & 0x80) === 0) {
        return result;
      }
      multiplier *= 128;
    }
    throw new Error('Thrift varint is too long for a 32-bit value');
  }

  // 64-bit values are never interpreted, so they are kept as their exact varint bytes.
  private readVarintBytes(): Uint8Array {
    const start = this.position;
    for (let index = 0; index < 10; index += 1) {
      if ((this.readByte() & 0x80) === 0) {
        return this.bytes.slice(start, this.position);
      }
    }
    throw new Error('Thrift varint is too long for a 64-bit value');
  }

  private readZigzag32(): number {
    const encoded = this.readVarintNumber();
    return (encoded >>> 1) ^ -(encoded & 1);
  }

  private readBinary(): Uint8Array {
    const length = this.readVarintNumber();
    if (this.position + length > this.bytes.length) {
      throw new Error('Thrift binary field runs past the end of the data');
    }
    const value = this.bytes.slice(this.position, this.position + length);
    this.position += length;
    return value;
  }

  private readValue(compactType: number, depth: number, inList: boolean): ThriftNode {
    if (depth > MAX_DEPTH) {
      throw new Error('Thrift structure is nested too deeply');
    }
    switch (compactType) {
      case COMPACT_BOOL_TRUE:
      case COMPACT_BOOL_FALSE:
        if (inList) {
          const listByte = this.readByte();
          return { type: 'bool', value: listByte === COMPACT_BOOL_TRUE, listByte };
        }
        return { type: 'bool', value: compactType === COMPACT_BOOL_TRUE };
      case COMPACT_BYTE:
        return { type: 'byte', value: this.readByte() };
      case COMPACT_I16:
        return { type: 'i16', value: this.readZigzag32() };
      case COMPACT_I32:
        return { type: 'i32', value: this.readZigzag32() };
      case COMPACT_I64:
        return { type: 'i64', varint: this.readVarintBytes() };
      case COMPACT_DOUBLE: {
        if (this.position + 8 > this.bytes.length) {
          throw new Error('Unexpected end of Thrift data');
        }
        const value = this.bytes.slice(this.position, this.position + 8);
        this.position += 8;
        return { type: 'double', bytes: value };
      }
      case COMPACT_BINARY:
        return { type: 'binary', bytes: this.readBinary() };
      case COMPACT_LIST:
      case COMPACT_SET: {
        const header = this.readByte();
        const elementType = header & 0x0f;
        const size = header >> 4 === 15 ? this.readVarintNumber() : header >> 4;
        const items: ThriftNode[] = [];
        for (let index = 0; index < size; index += 1) {
          items.push(this.readValue(elementType, depth + 1, true));
        }
        return { type: compactType === COMPACT_LIST ? 'list' : 'set', elementType, items };
      }
      case COMPACT_MAP: {
        const size = this.readVarintNumber();
        if (size === 0) {
          return { type: 'map', keyType: 0, valueType: 0, entries: [] };
        }
        const types = this.readByte();
        const keyType = types >> 4;
        const valueType = types & 0x0f;
        const entries: Array<[ThriftNode, ThriftNode]> = [];
        for (let index = 0; index < size; index += 1) {
          entries.push([this.readValue(keyType, depth + 1, true), this.readValue(valueType, depth + 1, true)]);
        }
        return { type: 'map', keyType, valueType, entries };
      }
      case COMPACT_STRUCT:
        return this.readStruct(depth + 1);
      default:
        throw new Error(`Unsupported Thrift compact type ${compactType}`);
    }
  }

  readStruct(depth = 0): ThriftStruct {
    const fields: ThriftField[] = [];
    let previousId = 0;
    for (;;) {
      const header = this.readByte();
      if (header === 0) {
        return { type: 'struct', fields };
      }
      const compactType = header & 0x0f;
      const delta = header >> 4;
      const id = delta === 0 ? this.readZigzag32() : previousId + delta;
      fields.push({ id, node: this.readValue(compactType, depth, false) });
      previousId = id;
    }
  }
}

class Writer {
  private buffer = new Uint8Array(1024);

  private length = 0;

  private ensure(extra: number) {
    if (this.length + extra <= this.buffer.length) {
      return;
    }
    let capacity = this.buffer.length * 2;
    while (capacity < this.length + extra) {
      capacity *= 2;
    }
    const next = new Uint8Array(capacity);
    next.set(this.buffer.subarray(0, this.length));
    this.buffer = next;
  }

  private writeByte(value: number) {
    this.ensure(1);
    this.buffer[this.length++] = value;
  }

  private writeBytes(value: Uint8Array) {
    this.ensure(value.length);
    this.buffer.set(value, this.length);
    this.length += value.length;
  }

  private writeVarintNumber(value: number) {
    let remaining = value;
    while (remaining >= 128) {
      this.writeByte(remaining % 128 | 0x80);
      remaining = Math.floor(remaining / 128);
    }
    this.writeByte(remaining);
  }

  private writeZigzag32(value: number) {
    this.writeVarintNumber(((value << 1) ^ (value >> 31)) >>> 0);
  }

  private compactTypeOf(node: ThriftNode): number {
    switch (node.type) {
      case 'bool':
        return node.value ? COMPACT_BOOL_TRUE : COMPACT_BOOL_FALSE;
      case 'byte':
        return COMPACT_BYTE;
      case 'i16':
        return COMPACT_I16;
      case 'i32':
        return COMPACT_I32;
      case 'i64':
        return COMPACT_I64;
      case 'double':
        return COMPACT_DOUBLE;
      case 'binary':
        return COMPACT_BINARY;
      case 'list':
        return COMPACT_LIST;
      case 'set':
        return COMPACT_SET;
      case 'map':
        return COMPACT_MAP;
      default:
        return COMPACT_STRUCT;
    }
  }

  private writeValue(node: ThriftNode) {
    switch (node.type) {
      case 'bool':
        if (node.listByte !== undefined) {
          this.writeByte(node.listByte);
        }
        return;
      case 'byte':
        this.writeByte(node.value);
        return;
      case 'i16':
      case 'i32':
        this.writeZigzag32(node.value);
        return;
      case 'i64':
        this.writeBytes(node.varint);
        return;
      case 'double':
        this.writeBytes(node.bytes);
        return;
      case 'binary':
        this.writeVarintNumber(node.bytes.length);
        this.writeBytes(node.bytes);
        return;
      case 'list':
      case 'set':
        if (node.items.length < 15) {
          this.writeByte((node.items.length << 4) | node.elementType);
        } else {
          this.writeByte(0xf0 | node.elementType);
          this.writeVarintNumber(node.items.length);
        }
        node.items.forEach((item) => this.writeValue(item));
        return;
      case 'map':
        this.writeVarintNumber(node.entries.length);
        if (node.entries.length > 0) {
          this.writeByte((node.keyType << 4) | node.valueType);
          node.entries.forEach(([key, value]) => {
            this.writeValue(key);
            this.writeValue(value);
          });
        }
        return;
      default:
        this.writeStruct(node);
    }
  }

  writeStruct(struct: ThriftStruct) {
    let previousId = 0;
    struct.fields.forEach(({ id, node }) => {
      const compactType = this.compactTypeOf(node);
      const delta = id - previousId;
      if (delta > 0 && delta <= 15) {
        this.writeByte((delta << 4) | compactType);
      } else {
        this.writeByte(compactType);
        this.writeZigzag32(id);
      }
      this.writeValue(node);
      previousId = id;
    });
    this.writeByte(0);
  }

  finish(): Uint8Array {
    return this.buffer.slice(0, this.length);
  }
}

export const decodeThriftStruct = (bytes: Uint8Array): ThriftStruct => {
  const reader = new Reader(bytes);
  const struct = reader.readStruct();
  if (reader.offset !== bytes.length) {
    throw new Error('Unexpected data after the Thrift structure');
  }
  return struct;
};

export const encodeThriftStruct = (struct: ThriftStruct): Uint8Array => {
  const writer = new Writer();
  writer.writeStruct(struct);
  return writer.finish();
};
